import React, { useState } from 'react';
import { 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Users, 
  MapPin, 
  Phone, 
  Mail,
  Target,
  TrendingUp,
  BookOpen,
  FileText,
  MessageSquare,
  DollarSign
} from 'lucide-react';
import { Unit } from '../../types';
import { mockUnits, mockBranches, getUnitTypeName } from '../../data/mockData';
import AddUnitModal from '../Modals/AddUnitModal';
import UnitDetailModal from '../Modals/UnitDetailModal';

const Units: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [units, setUnits] = useState<Unit[]>(mockUnits);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);

  const getUnitIcon = (type: string) => {
    switch (type) {
      case 'education_consultant': return BookOpen;
      case 'visa_consultant': return FileText;
      case 'marketing': return TrendingUp;
      case 'sales': return Target;
      case 'customer_service': return MessageSquare;
      case 'accounting': return DollarSign;
      default: return Users;
    }
  };

  const getUnitColor = (type: string) => {
    switch (type) {
      case 'education_consultant': return 'from-green-500 to-blue-500';
      case 'visa_consultant': return 'from-purple-500 to-blue-500';
      case 'marketing': return 'from-pink-500 to-purple-500';
      case 'sales': return 'from-orange-500 to-red-500';
      case 'customer_service': return 'from-blue-500 to-cyan-500';
      case 'accounting': return 'from-green-500 to-emerald-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = 
      unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.manager.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || unit.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  const handleSaveUnit = (unitData: any) => {
    setUnits(prev => [unitData, ...prev]);
  };

  const handleUnitClick = (unit: Unit) => {
    setSelectedUnit(unit);
    setShowDetailModal(true);
  };

  const handleEditUnit = (unit: Unit) => {
    console.log('Birim düzenleniyor:', unit);
    setShowDetailModal(false);
    // Burada düzenleme modalı açılacak
  };

  const handleAddEmployee = (unitId: string) => {
    console.log('Çalışan ekleniyor, birim:', unitId);
    setShowDetailModal(false);
    // Burada çalışan ekleme modalı açılacak
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Birimler</h2>
          <p className="text-gray-600 dark:text-gray-400">Organizasyon birimlerinizi yönetin</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Birim</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">6</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Aktif Birim</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">245</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Öğrenci</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">%92</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Hedef Başarı</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">83</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bu Ay Başvuru</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Birim ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Birim Türleri</option>
            <option value="education_consultant">Eğitim Danışmanlığı</option>
            <option value="visa_consultant">Vize Danışmanlığı</option>
            <option value="marketing">Pazarlama</option>
            <option value="sales">Satış</option>
            <option value="customer_service">Müşteri Hizmetleri</option>
            <option value="accounting">Muhasebe</option>
          </select>

          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg transition-colors">
            Performans Raporu
          </button>
        </div>
      </div>

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUnits.map((unit) => {
          const IconComponent = getUnitIcon(unit.type);
          const gradientColor = getUnitColor(unit.type);
          
          return (
            <div key={unit.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 bg-gradient-to-br ${gradientColor} rounded-xl flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{unit.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{getUnitTypeName(unit.type)}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  unit.isActive 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                }`}>
                  {unit.isActive ? 'Aktif' : 'Pasif'}
                </span>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Users className="w-4 h-4" />
                  <span>Yönetici: {unit.manager}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Phone className="w-4 h-4" />
                  <span>{unit.phone}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{unit.email}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{unit.totalStudents}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Öğrenci</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{unit.currentMonthApplications}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Bu Ay</p>
                </div>
              </div>

              {unit.monthlyTarget > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Aylık Hedef</span>
                    <span className="text-gray-900 dark:text-white">{unit.currentMonthApplications}/{unit.monthlyTarget}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${Math.min((unit.currentMonthApplications / unit.monthlyTarget) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {unit.specializations && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Uzmanlık Alanları:</p>
                  <div className="flex flex-wrap gap-1">
                    {unit.specializations.slice(0, 2).map((spec, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 text-xs rounded-full">
                        {spec}
                      </span>
                    ))}
                    {unit.specializations.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 text-xs rounded-full">
                        +{unit.specializations.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => handleUnitClick(unit)}
                  className="flex-1 flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-2 rounded-lg transition-colors"
                  title="Birim detaylarını görüntüle"
                >
                  <Eye className="w-4 h-4" />
                  <span>Detay</span>
                </button>
                <button 
                  onClick={() => console.log('Birim düzenleme:', unit.id)}
                  className="flex-1 flex items-center justify-center space-x-2 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-2 rounded-lg transition-colors"
                  title="Birim bilgilerini düzenle"
                >
                  <Edit className="w-4 h-4" />
                  <span>Düzenle</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Add Unit Modal */}
      <AddUnitModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveUnit}
      />
      
      {/* Unit Detail Modal */}
      {selectedUnit && (
        <UnitDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedUnit(null);
          }}
          unit={selectedUnit}
          onEdit={handleEditUnit}
          onAddEmployee={handleAddEmployee}
        />
      )}
    </div>
  );
};

export default Units;